<template>
  <main class="container mx-auto px-4 py-8">
    <h1 class="text-3xl font-bold mb-8">{{ categoryName }}</h1>

    <div class="flex flex-col md:flex-row gap-8">
      <!-- Sidebar with filters -->
      
      <div class="w-full">
        <div v-if="loading" class="flex justify-center items-center min-h-screen">
          <div class="loading">
            <span></span>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
        <div v-else-if="error" class="text-center text-red-500">{{ error.message }}</div>
        <div v-else-if="filteredProducts.length" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div 
            v-for="product in filteredProducts" 
            :key="product.id" 
            class="bg-white rounded-xl shadow-lg overflow-hidden transform transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 group"
          >
            <!-- Image Container with Overlay -->
            <div class="relative overflow-hidden">
              <img 
                v-if="product.img"
                :src="`${API_BASE_URL}/storage/${product.img.replace('public/', '')}`"  
                :alt="product.name" 
                class="w-full h-56 object-cover transform transition-transform duration-500 group-hover:scale-110"
              >
              <div class="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>

            <!-- Content Container -->
            <div class="p-6 space-y-4">
              <!-- Product Name -->
              <h3 class="text-xl font-bold text-gray-800 group-hover:text-blue-600 transition-colors duration-300">
                {{ product.name }}
              </h3>

              <!-- Description -->
              <p class="text-sm text-gray-600 line-clamp-2 min-h-[40px]">
                {{ product.description }}
              </p>

              <!-- Dimensions with Icon -->
              <div class="flex items-center space-x-2 text-gray-700">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                </svg>
                <p class="text-sm font-medium">
                  {{ product.Largeur }} (L)  x {{ product.Hauteur }} (H) x {{ product.Profondeur }} (P) mm
                </p>  
              </div>

              <!-- Button Container -->
              <div class="pt-4">
                <RouterLink :to="buildProductPath(product)" class="block">
                  <button class="w-full px-6 py-3 bg-blue-600 text-white rounded-lg shadow-md 
                               hover:bg-blue-700 active:bg-blue-800 
                               transform transition-all duration-300
                               hover:shadow-lg hover:-translate-y-0.5
                               focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                               flex items-center justify-center space-x-2 group">
                    <span>En savoir plus</span>
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      class="h-5 w-5 transform transition-transform duration-300 group-hover:translate-x-1" 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </button>
                </RouterLink>
              </div>
            </div>
          </div>
        </div>
        <p v-else class="text-center text-gray-500 text-lg">
          Aucun produit trouvé pour les filtres sélectionnés.
        </p>
      </div>
    </div>
  </main>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useDataStore } from '@/stores/mainStore';
import { API_BASE_URL } from '@/apiConfig';
import { useHead } from '@vueuse/head';

const route = useRoute();
const dataStore = useDataStore();

const categoryId = route.params.id;
const categoryName = ref('');

const slugify = (value) =>
  String(value || '')
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const buildProductPath = (product) => `/product/${slugify(product.name)}-${product.id}`;

useHead({
  title: computed(() => categoryName.value ? `${categoryName.value} | Mega Hissotto – Climatisation Algérie` : 'Produits par Catégorie | Mega Hissotto'),
  meta: [
    { name: 'description', content: computed(() => `Découvrez tous les produits Mega Hissotto dans la catégorie ${categoryName.value || 'climatisation'} : compresseurs INGOTECH, Panasonic, pompes à chaleur, refroidisseurs et plus encore en Algérie.`) },
    { property: 'og:title', content: computed(() => `${categoryName.value || 'Catégorie'} | Mega Hissotto`) },
  ]
});
const loading = computed(() => dataStore.loading);
const error = computed(() => dataStore.error);

const dimensionLabels = {
  width: 'Largeur',
  height: 'Hauteur',
  depth: 'Profondeur',
};

const dimensions = ref({
  width: { min: null, max: null },
  height: { min: null, max: null },
  depth: { min: null, max: null },
});

const filteredProducts = computed(() => {
  return dataStore.data.products.filter(product => {
    if (product.category_id !== Number.parseInt(categoryId, 10)) {
      return false;
    }
    for (const [dim, range] of Object.entries(dimensions.value)) {
      const productDimension = product[dimensionLabels[dim].toLowerCase()];
      if (range.min !== null && productDimension < range.min) {
        return false;
      }
      if (range.max !== null && productDimension > range.max) {
        return false;
      }
    }
    return true;
  });
});

onMounted(async () => {
  await dataStore.fetchData();
  const category = dataStore.data.categories.find(cat => cat.id === Number.parseInt(categoryId, 10));
  if (category) {
    categoryName.value = category.name;
  }
});
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Loading Spinner */
.loading {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100px;
  gap: 6px;
  height: 100px;
}
</style>